import { Action as ReduxAction } from 'redux';
import { ThunkAction as ReduxThunkAction, ThunkDispatch } from 'redux-thunk';
import { useDispatch } from 'react-redux';
import { insertTrackEvent } from '@comeon/analytics';
import { LOGOUT } from '@comeon/actions-public';
import reducerRegistry from '@comeon/reducer-registry';
import { showNotification } from '../../../notifications/ducks/notifications';
import { fetchSubscriptions, subscribe, unsubscribe, Widget } from './api';

interface ExclusiveGameWidgetsReducerState {
	subscriptions: Widget[] | null;
	modalOpen: boolean;
}

export type RootState = {
	[sliceName: string]: unknown;
	brand: {
		siteName: string;
	};
	exclusiveGameWidgets: ExclusiveGameWidgetsReducerState;
};

type ThunkAction<
	Actions extends ReduxAction = ExclusiveGameWidgetsActions,
	ReturnType = void,
	ExtraArgs = unknown
> = ReduxThunkAction<ReturnType, RootState, ExtraArgs, Actions>;

const ACTION_PREFIX = '@komigen/exclusive-game-widgets';
const REDUCER_NAME = 'exclusiveGameWidgets';

const EXCLUSIVE_GAME_WIDGET_SUBSCRIBE = `${ACTION_PREFIX}/SUBSCRIBE`;
const EXCLUSIVE_GAME_WIDGET_UNSUBSCRIBE = `${ACTION_PREFIX}/UNSUBSCRIBE`;
export const EXCLUSIVE_GAME_WIDGET_UPDATE_SUBSCRIPTIONS = `${ACTION_PREFIX}/UPDATE_SUBSCRIPTIONS`;
const EXCLUSIVE_GAME_WIDGET_TOGGLE_MODAL_OPEN = `${ACTION_PREFIX}/TOGGLE_MODAL_OPEN`;

export const subscribeGameWidget =
	(widget: Widget, isLiveCasinoLocation: boolean): ThunkAction =>
	async dispatch => {
		dispatch({ type: EXCLUSIVE_GAME_WIDGET_SUBSCRIBE, payload: widget } as const);

		const response = await subscribe(widget);

		if (response?.status === 'FAILURE') {
			dispatch(
				showNotification({
					message: response.errorDescription,
					color: 'red'
				})
			);
			dispatch({ type: EXCLUSIVE_GAME_WIDGET_UNSUBSCRIBE, payload: widget } as const);
			return;
		}

		if (!response) {
			dispatch(
				showNotification({
					messageKey: 'WIDGET_SUBSCRIPTION_FAILED',
					color: 'red'
				})
			);
			dispatch({ type: EXCLUSIVE_GAME_WIDGET_UNSUBSCRIBE, payload: widget } as const);
			return;
		}

		insertTrackEvent({
			category: isLiveCasinoLocation ? 'LiveCasino' : 'Casino',
			action: 'WidgetExclusive_Click_RemindMe',
			label: 'Reminder set',
			value: ''
		});
	};

export const unsubscribeGameWidget =
	(widget: Widget): ThunkAction =>
	async dispatch => {
		dispatch({ type: EXCLUSIVE_GAME_WIDGET_UNSUBSCRIBE, payload: widget } as const);

		const response = await unsubscribe(widget);

		if (response?.status === 'FAILURE') {
			dispatch(
				showNotification({
					message: response.errorDescription,
					color: 'red'
				})
			);
			dispatch({ type: EXCLUSIVE_GAME_WIDGET_SUBSCRIBE, payload: widget } as const);

			return;
		}

		if (!response) {
			dispatch(
				showNotification({
					messageKey: 'WIDGET_UNSUBSCRIBE_FAILED',
					color: 'red'
				})
			);
			dispatch({ type: EXCLUSIVE_GAME_WIDGET_SUBSCRIBE, payload: widget } as const);
		}
	};

export const updateSubscriptions = (payload: Widget[] | null) =>
	({
		type: EXCLUSIVE_GAME_WIDGET_UPDATE_SUBSCRIPTIONS,
		payload
	} as const);

export const getSubscriptions =
	(playerId: string): ThunkAction =>
	async (dispatch, getState) => {
		const { subscriptions } = getState().exclusiveGameWidgets;
		if (subscriptions) return;

		const response = await fetchSubscriptions(playerId);

		if (response?.status === 'FAILURE' || !response) {
			return;
		}

		dispatch(updateSubscriptions(response.result || []));
	};

export const toggleModalOpen = (payload: boolean) =>
	({ type: EXCLUSIVE_GAME_WIDGET_TOGGLE_MODAL_OPEN, payload } as const);

const initialState: ExclusiveGameWidgetsReducerState = {
	subscriptions: null,
	modalOpen: false
};

export type ExclusiveGameWidgetsActions =
	| {
			type: typeof EXCLUSIVE_GAME_WIDGET_SUBSCRIBE;
			payload: Widget;
	  }
	| {
			type: typeof EXCLUSIVE_GAME_WIDGET_UNSUBSCRIBE;
			payload: Widget;
	  }
	| {
			type: typeof EXCLUSIVE_GAME_WIDGET_UPDATE_SUBSCRIPTIONS;
			payload: Widget[] | null;
	  }
	| {
			type: typeof EXCLUSIVE_GAME_WIDGET_TOGGLE_MODAL_OPEN;
			payload: boolean;
	  };

const reducer = (
	state = initialState, // eslint-disable-line @typescript-eslint/default-param-last
	{ type, payload }: ExclusiveGameWidgetsActions
) => {
	switch (type) {
		case EXCLUSIVE_GAME_WIDGET_SUBSCRIBE:
			return {
				...state,
				subscriptions: [...(state.subscriptions ?? []), payload]
			};
		case EXCLUSIVE_GAME_WIDGET_UNSUBSCRIBE:
			return {
				...state,
				subscriptions: (state.subscriptions ?? []).filter(
					subscription => subscription.widgetId !== payload.widgetId
				)
			};
		case EXCLUSIVE_GAME_WIDGET_UPDATE_SUBSCRIPTIONS:
			return {
				...state,
				subscriptions: payload
			};

		case EXCLUSIVE_GAME_WIDGET_TOGGLE_MODAL_OPEN:
			return {
				...state,
				modalOpen: payload
			};
		case LOGOUT:
			return initialState;
		default:
			return state;
	}
};

reducerRegistry.register(REDUCER_NAME, reducer);
export type State = ReturnType<typeof reducer>;
export const useExclusiveGameWidgetsDispatch: () => ThunkDispatch<RootState, boolean, ExclusiveGameWidgetsActions> =
	useDispatch;
export default reducer;
